/**
 * O convite para ligar os avisos do navegador, depois do login (#674).
 *
 * Quando aparece, e o "Agora não" que volta em um dia, estão em
 * `convite-dos-avisos.ts`.
 *
 * ── "Ativar avisos" é o gesto que o navegador exige ──────────────────────
 *
 * O pedido de permissão sai PRIMEIRO, direto do clique: qualquer espera antes
 * dele (buscar a chave do push, por exemplo) pode gastar a ativação do gesto,
 * e o navegador recusa o pedido sem mostrar nada. Com a permissão dada, liga a
 * preferência e registra a inscrição de push (`POST /v1/me/dispositivos`). Sem
 * push no servidor, o aviso com o CRM em outra aba continua valendo.
 *
 * Negado no pedido, a preferência NÃO é gravada: a faixa passa a explicar onde
 * liberar, em vez de sumir sem dizer que nada foi ligado.
 */

import { useState, type ReactNode } from 'react';
import { useApi } from '@/shared/api/consultas';
import { ligarPush } from '../lib/push';
import {
  COMO_LIBERAR_O_AVISO,
  pedirPermissaoDoAviso,
  usePermissaoDoAviso,
} from '../model/aviso-do-sistema';
import {
  adiarConvite,
  lerAdiamentoDoConvite,
  situacaoDoConvite,
} from '../model/convite-dos-avisos';
import {
  definirPreferencia,
  preferenciaDecidida,
  usePreferenciasDeNotificacao,
} from '../model/preferencias';
import './convite-dos-avisos.css';

export function ConviteDosAvisos({
  usuarioId,
  recebeNotificacao,
}: {
  usuarioId: string;
  /** O papel lê conversa? Sem isso não há aviso para ligar. */
  recebeNotificacao: boolean;
}): ReactNode {
  const api = useApi();
  const permissao = usePermissaoDoAviso();
  // Relê `preferenciaDecidida` quando a escolha muda, aqui ou em outra aba.
  usePreferenciasDeNotificacao();
  const [adiadoAte, definirAdiadoAte] = useState(() => lerAdiamentoDoConvite(usuarioId));
  const [ocupado, definirOcupado] = useState(false);

  const situacao = situacaoDoConvite({
    recebeNotificacao,
    permissao,
    decidida: preferenciaDecidida('foraDaTela'),
    adiadoAte,
    agora: Date.now(),
  });
  if (situacao === 'nenhum') return null;

  const ativar = async (): Promise<void> => {
    definirOcupado(true);
    try {
      if ((await pedirPermissaoDoAviso()) !== 'granted') return;
      definirPreferencia('foraDaTela', true);
      // O push é a parte "com o CRM fechado"; falhar nele não desfaz o resto.
      if (api) await ligarPush(api).catch(() => undefined);
    } finally {
      definirOcupado(false);
    }
  };

  return (
    <section className="convite-dos-avisos" aria-label="Avisos do navegador">
      <p className="convite-dos-avisos__texto">
        {situacao === 'convidar'
          ? 'Receba os avisos de mensagem mesmo com o CRM em outra aba.'
          : `O navegador bloqueou os avisos de mensagem deste site. ${COMO_LIBERAR_O_AVISO}`}
      </p>
      <div className="convite-dos-avisos__acoes">
        {situacao === 'convidar' && (
          <button
            type="button"
            className="convite-dos-avisos__acao"
            disabled={ocupado}
            onClick={() => void ativar()}
          >
            Ativar avisos
          </button>
        )}
        <button
          type="button"
          className="convite-dos-avisos__acao convite-dos-avisos__acao--discreta"
          onClick={() => definirAdiadoAte(adiarConvite(usuarioId))}
        >
          Agora não
        </button>
      </div>
    </section>
  );
}
